<template>
  <div class="pp-page">
    <header class="pp-topbar">
      <a href="/" class="pp-topbar__brand" title="Hireclap">
        <img
          class="pp-topbar__logo"
          src="/hireclap-logo-small.webp"
          alt="Hireclap"
          width="140"
          height="40"
        />
      </a>
    </header>

    <div v-if="user_loading" class="pp-page__container">
      <div class="pp-skeleton-hero" aria-busy="true" aria-label="Loading profile">
        <content-loader viewBox="0 0 1120 200" primaryColor="#f1f5f9" secondaryColor="#e2e8f0">
          <rect x="24" y="24" rx="12" ry="12" width="128" height="128" />
          <rect x="176" y="36" rx="4" ry="4" width="260" height="28" />
          <rect x="176" y="76" rx="4" ry="4" width="200" height="16" />
          <rect x="176" y="108" rx="8" ry="8" width="120" height="28" />
          <rect x="308" y="108" rx="8" ry="8" width="100" height="28" />
        </content-loader>
      </div>
    </div>

    <template v-else-if="user && user.id">
      <PublicProfileHero
        :user="user"
        :headline="headline"
        :job-roles="jobRoles"
        :initials="initials"
        :location-label="locationLabel"
        :experience-label="experienceLabel"
        :education-label="educationChipLabel"
        :visume="visume"
        :visume-path="visumePath"
        :show-education-section="showEducation"
        :show-experience-section="showExperience"
        @open-visume="openVisumeModal"
      />

      <div class="pp-page__container">
        <div class="pp-layout">
          <div class="pp-layout__main">
            <nav v-if="visibleSections.length > 1" class="pp-nav" aria-label="Profile sections">
              <button
                v-for="item in visibleSections"
                :key="item.id"
                type="button"
                class="pp-nav__link"
                :class="{ 'pp-nav__link--active': activeSection === item.id }"
                @click="scrollToSection(item.id)"
              >{{ item.label }}</button>
            </nav>

            <PublicProfileResume
              v-if="showResume"
              :resume-url="resumeUrl"
              :viewer-type="resumeViewerType"
              :file-name="resumeFileName"
            />

            <PublicProfileAbout v-if="showAbout" :summary="aboutSummary" />

            <PublicProfileExperience
              v-if="showExperience"
              :current-company="validCurrentCompany"
              :previous-companies="validPreviousCompanies"
              :experience-label="experienceLabel"
            />

            <PublicProfileEducation
              v-if="showEducation"
              :highest-education="highestEducation"
              :school-qualifications="schoolQualifications"
            />
          </div>

          <PublicProfileSidebar
            v-if="showSidebar"
            class="pp-layout__aside"
            :job-roles="jobRoles"
            :skills="skills"
            :highest-education="highestEducation"
            :languages="languages"
            :communication-label="communicationLabel"
            :hide-roles-on-mobile="jobRoles.length > 0"
            :hide-education-on-mobile="showEducation"
          />
        </div>

        <p class="pp-powered">
          Powered by <a href="/">Hireclap</a>
        </p>
      </div>
    </template>

    <div v-else class="pp-page__container">
      <div class="pp-section pp-empty">
        <p><strong>Profile not found</strong></p>
        <p>This candidate profile may have been removed or the link is incorrect.</p>
        <a href="/jobs" class="pp-btn pp-btn--primary" style="margin-top:1rem;">Browse jobs</a>
      </div>
    </div>

    <PublicProfileVisumeModal
      ref="visumeModal"
      :candidate-name="user.name || ''"
      :visume-url="visume ? $S3_bucket_url + visume : ''"
    />
  </div>
</template>

<script>
import PublicProfileHero from '@/components/PublicProfile/PublicProfileHero.vue';
import PublicProfileAbout from '@/components/PublicProfile/PublicProfileAbout.vue';
import PublicProfileExperience from '@/components/PublicProfile/PublicProfileExperience.vue';
import PublicProfileEducation from '@/components/PublicProfile/PublicProfileEducation.vue';
import PublicProfileResume from '@/components/PublicProfile/PublicProfileResume.vue';
import PublicProfileSidebar from '@/components/PublicProfile/PublicProfileSidebar.vue';
import PublicProfileVisumeModal from '@/components/PublicProfile/PublicProfileVisumeModal.vue';
import { ContentLoader } from 'vue-content-loader';
import { mapState, mapGetters } from 'vuex';
import Actions from '@/libraries/apiActions.js';
import store from '@/store';
import { JOB_ROLE } from '@/store/modules/jobrole/constants';
import {
  extractCandidateId,
  parseSkills,
  parseLanguages,
  getExperienceLabel,
  getCommunicationLabel,
  resolveJobRoles,
  getLocationLabel,
  getEducationSummary,
  getSchoolQualifications,
  getNameInitials,
  buildAutoTagline,
  getValidCurrentCompany,
  getValidPreviousCompanies,
  getResumeViewerType,
  getResumeFileName,
} from '@/utils/publicProfile';

import '@/assets/hire.css';
import '@/assets/public-profile.css';

export default {
  name: 'PublicProfile',
  components: {
    PublicProfileHero,
    PublicProfileAbout,
    PublicProfileExperience,
    PublicProfileEducation,
    PublicProfileResume,
    PublicProfileSidebar,
    PublicProfileVisumeModal,
    ContentLoader,
  },
  metaInfo() {
    if (!this.user?.name) {
      return { title: 'Candidate profile — Hireclap' };
    }
    const title = `${this.user.name}${this.headline ? ' — ' + this.headline : ''}`;
    const description = this.aboutSummary || this.tagline
      ? (this.aboutSummary || this.tagline).slice(0, 155) + ((this.aboutSummary || this.tagline).length > 155 ? '…' : '')
      : `View ${this.user.name}'s professional profile on Hireclap.`;
    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'profile' },
        { property: 'og:url', content: typeof window !== 'undefined' ? window.location.href : '' },
      ],
    };
  },
  beforeCreate() {
    this.$store = store;
  },
  created() {
    const apiFn = this.action;
    this.cachedDispatch(JOB_ROLE.GET_JOB_ROLE, { param: apiFn });
  },
  beforeMount() {
    window.scrollTo(0, 0);
    const id = extractCandidateId(this.$route.params.user);
    if (!id) {
      this.user_loading = false;
      return;
    }
    this.action.getCandidateProfile(id).then((res) => {
      this.user = res.candidate || {};
      this.user_loading = false;
    }).catch(() => {
      this.user_loading = false;
      this.user = {};
    });
  },
  mounted() {
    this._onScroll = () => this.updateActiveSection();
    window.addEventListener('scroll', this._onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll);
  },
  computed: {
    ...mapGetters(['cachedDispatch']),
    ...mapState({
      jobroleList: (state) => state.jobrole.jobrole.jobrole || [],
    }),
    aboutSummary() {
      return this.user?.personal_details?.resume_summary || '';
    },
    skills() {
      return parseSkills(this.user?.personal_details?.skills);
    },
    languages() {
      return parseLanguages(this.user);
    },
    jobRoles() {
      return resolveJobRoles(this.user?.careerPreferencesDetail, this.jobroleList);
    },
    headline() {
      return this.jobRoles[0] || '';
    },
    initials() {
      return getNameInitials(this.user?.name);
    },
    tagline() {
      if (this.aboutSummary) return '';
      return buildAutoTagline({
        name: this.user?.name,
        experienceLabel: this.experienceLabel,
        locationLabel: this.locationLabel,
        jobRoles: this.jobRoles,
        hasHeadline: !!this.headline,
      }) || '';
    },
    locationLabel() {
      return getLocationLabel(this.user, this.user?.personal_details?.user_city);
    },
    experienceLabel() {
      const months = this.user?.personal_details?.total_work_experience;
      return getExperienceLabel(months);
    },
    educationChipLabel() {
      const edu = getEducationSummary(this.user);
      return edu?.title || null;
    },
    highestEducation() {
      return getEducationSummary(this.user);
    },
    schoolQualifications() {
      return getSchoolQualifications(this.user);
    },
    communicationLabel() {
      return getCommunicationLabel(this.user?.personal_details?.communication_rating);
    },
    visume() {
      return this.user?.personal_details?.video_resume_path || '';
    },
    visumePath() {
      return this.visume ? `${this.visume}#t=0.1` : '';
    },
    resumeUrl() {
      const path = this.user?.personal_details?.resume_path;
      return path ? this.$S3_bucket_url + path : '';
    },
    resumePath() {
      return this.user?.personal_details?.resume_path || '';
    },
    resumeViewerType() {
      return getResumeViewerType(this.resumePath);
    },
    resumeFileName() {
      return getResumeFileName(this.resumePath);
    },
    showResume() {
      return !!(this.resumeUrl && this.resumeViewerType);
    },
    validCurrentCompany() {
      return getValidCurrentCompany(this.user?.current_company);
    },
    validPreviousCompanies() {
      return getValidPreviousCompanies(this.user?.previous_companies);
    },
    showAbout() {
      return !!this.aboutSummary;
    },
    showExperience() {
      return !!(this.validCurrentCompany || this.validPreviousCompanies.length);
    },
    showEducation() {
      return !!(this.highestEducation || this.schoolQualifications.length);
    },
    showSidebar() {
      return !!(
        this.jobRoles.length ||
        this.skills.length ||
        this.highestEducation ||
        this.languages.length ||
        this.communicationLabel
      );
    },
    visibleSections() {
      const sections = [];
      if (this.showResume) sections.push({ id: 'pp-resume', label: 'Resume' });
      if (this.showAbout) sections.push({ id: 'pp-about', label: 'About' });
      if (this.showExperience) sections.push({ id: 'pp-experience', label: 'Experience' });
      if (this.showEducation) sections.push({ id: 'pp-education', label: 'Education' });
      return sections;
    },
  },
  data() {
    return {
      action: new Actions(),
      user: {},
      user_loading: true,
      activeSection: 'pp-about',
    };
  },
  watch: {
    visibleSections: {
      immediate: true,
      handler(sections) {
        if (sections.length && !sections.find((s) => s.id === this.activeSection)) {
          this.activeSection = sections[0].id;
        }
      },
    },
  },
  methods: {
    openVisumeModal() {
      if (!this.visume) return;
      this.$refs.visumeModal.show();
    },
    scrollToSection(id) {
      const el = document.getElementById(id);
      if (el) {
        const offset = 72;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      this.activeSection = id;
    },
    updateActiveSection() {
      const offset = 88;
      let current = this.activeSection;
      this.visibleSections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= offset) {
          current = section.id;
        }
      });
      this.activeSection = current;
    },
  },
};
</script>
